import { useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { default as RecentlyUpdatedMacroQuery } from './__generated__/RecentlyUpdatedMacroQuery.graphql.ts';
import type {
	RecentlyUpdatedMacroQuery$data,
	RecentlyUpdatedMacroQuery$variables,
} from './__generated__/RecentlyUpdatedMacroQuery.graphql.ts';
import { buildRecentlyUpdatedQueryVariables } from './utils/recentlyUpdatedQueryUtils.ts';
import type {
	ContentType,
	RecentlyUpdatedMacroParams,
	UpdateItem,
} from './utils/recently-updated-types.ts';

type SearchNode = NonNullable<
	NonNullable<NonNullable<RecentlyUpdatedMacroQuery$data['search']>['nodes']>[number]
>;

// ---------------------------------------------------------------------------
// Helpers to map GraphQL response → UpdateItem
// ---------------------------------------------------------------------------

function resolveContentType(type: string | null | undefined, url?: string): ContentType {
	const lower = (type ?? '').toLowerCase();
	if (lower === 'blogpost' || lower === 'blog_post') return 'blogpost';
	if (lower === 'comment') return 'comment';
	if (lower === 'attachment') return 'attachment';
	if (lower === 'whiteboard') return 'whiteboard';
	if (lower === 'database') return 'database';
	if (lower === 'embed') return 'embed';
	if (lower === 'userinfo') return 'userinfo';
	if (lower === 'folder') return 'folder';

	const urlLower = (url ?? '').toLowerCase();
	if (urlLower.includes('/whiteboard/')) return 'whiteboard';
	if (urlLower.includes('/database/')) return 'database';
	if (urlLower.includes('/embed/')) return 'embed';
	if (urlLower.includes('/folder/')) return 'folder';
	return 'page';
}

function formatTimestamp(
	timestamp: string | null | undefined,
	formatDate: IntlShape['formatDate'],
	timeZone: string | undefined,
): string {
	if (!timestamp) return '';
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) return timestamp;
	// `timeZone` must be passed explicitly. Confluence's IntlProvider does not set a
	// timeZone, so react-intl falls back to the ambient environment timezone — UTC in
	// the SSR container, the user's local zone in the browser. For timestamps near
	// midnight that renders a different day on each side of the hydration boundary,
	// which breaks hydration and forces a client re-render that hurts vc90. Sourcing
	// the zone from session data keeps SSR and client identical because both read the
	// same serialized `__SITEINFORMATION__` payload.
	return formatDate(date, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		timeZone,
	});
}

function nodeToUpdateItem(
	node: SearchNode | null | undefined,
	formatDate: IntlShape['formatDate'],
	timeZone: string | undefined,
): UpdateItem | null {
	if (!node) return null;

	const content = node.content;
	if (!content) return null;

	const status: string = content.status ?? '';
	if (status === 'archived' || status === 'trashed') return null;

	const id: string = content.id ?? '';
	const title: string = content.title ?? '';
	const rawUrl: string = node.url ?? content.links?.webui ?? '';
	const url: string = rawUrl && !rawUrl.startsWith('/wiki') ? `/wiki${rawUrl}` : rawUrl;
	const contentType = resolveContentType(content.type, url);
	const spaceKey: string = content.space?.key ?? '';
	const spaceName: string = content.space?.name ?? '';

	// Filter out drafts
	if (!title || url.includes('resumedraft.action')) return null;

	// Date: use lastcontributed.when if available, else fall back to search node's lastModified
	const timestamp =
		content.metadata?.currentuser?.lastcontributed?.when ?? node.lastModified ?? null;
	const formattedDate = formatTimestamp(timestamp, formatDate, timeZone);

	// Version number determines created vs updated label
	const version: number = content.version?.number ?? 2;

	// Author from version.by (accountId is on KnownUser inline fragment)
	const versionBy = content.version?.by;
	const authorAccountId: string = versionBy?.accountId ?? '';
	const author = {
		username: authorAccountId,
		displayName: versionBy?.displayName ?? '',
		profileUrl: authorAccountId ? `/wiki/display/~${encodeURIComponent(authorAccountId)}` : '',
		avatarUrl: versionBy?.profilePicture?.path ?? '',
	};

	return {
		id,
		title,
		url,
		contentType,
		version,
		formattedDate,
		author,
		spaceKey,
		spaceName,
	};
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type UseRecentlyUpdatedItemsResult = {
	items: UpdateItem[];
	loading: boolean;
	error: Error | undefined;
	hasNextPage: boolean;
	endCursor: string | null;
};

export function useRecentlyUpdatedItems(
	params: RecentlyUpdatedMacroParams,
	cursor: string | null = null,
): UseRecentlyUpdatedItemsResult {
	const { formatDate } = useIntl();
	const { timeZone: userTimeZone, tenantTimeZone } = useSessionData();
	// Session data is rendered on the server and serialized to the client, so this
	// resolves to the same zone on both sides of hydration. See formatTimestamp.
	const timeZone = userTimeZone || tenantTimeZone || undefined;
	const [pageContextSpaceKey] = usePageSpaceKey();
	const { author, labels, max, spaces, types, currentSpaceKey } = params;
	// The query variable builder resolves an explicit spaces param and translates @self
	// to this host space key. Prefer the handler context over page context so this stays
	// in parity with the SSR preload and the cache-first read below
	// hits the preloaded result instead of refetching and repainting after SSR (vc90).
	// The parity is locked by "SSR preload variable parity" tests in
	// useRecentlyUpdatedItems-test.ts — keep the two in sync.
	const hostSpaceKey = currentSpaceKey ?? pageContextSpaceKey ?? '';

	const variables = useMemo<RecentlyUpdatedMacroQuery$variables>(
		() =>
			buildRecentlyUpdatedQueryVariables(
				{
					author,
					labels,
					max,
					spaces,
					types,
				},
				hostSpaceKey,
				cursor ?? '',
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				fg('cc_rewrite_rec_updated_macro_fix_param_prefixes'),
			),
		[author, labels, max, spaces, types, hostSpaceKey, cursor],
	);

	const { data, loading, error } = useQueryInterop<
		RecentlyUpdatedMacroQuery$data,
		RecentlyUpdatedMacroQuery$variables
	>(RecentlyUpdatedMacroQuery, {
		variables,
		fetchPolicy: 'cache-first',
	});

	const items = useMemo<UpdateItem[]>(() => {
		const nodes = data?.search?.nodes ?? [];
		const result: UpdateItem[] = [];
		for (const node of nodes) {
			const item = nodeToUpdateItem(node, formatDate, timeZone);
			if (item !== null) result.push(item);
		}
		return result;
	}, [data, formatDate, timeZone]);

	const pageInfo = data?.search?.pageInfo;
	const hasNextPage: boolean = pageInfo?.hasNextPage ?? false;
	const endCursor: string | null = pageInfo?.endCursor ?? null;

	return { items, loading, error, hasNextPage, endCursor };
}
